import { useEffect, useRef, useState } from 'react';
import { Brain } from 'lucide-react';

interface Agent {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  type: 'coordinator' | 'worker' | 'specialist';
  connections: number[];
  activity: number;
  state: 'idle' | 'processing' | 'communicating' | 'learning';
  workload: number;
  lastTaskTime: number;
  energy: number;
  role: string;
}

interface Connection {
  from: Agent;
  to: Agent;
  strength: number;
  pulse: number;
}

const AIAgentNetwork = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize agents with realistic AI behaviors
    const initialAgents: Agent[] = [];
    const agentTypes: Array<'coordinator' | 'worker' | 'specialist'> = ['coordinator', 'worker', 'specialist'];
    
    // Define specific roles for each agent type
    const coordinatorRoles = ['Task Manager', 'Resource Allocator'];
    const workerRoles = ['Data Processor', 'Query Handler', 'File Analyzer', 'Pattern Detector', 'Content Generator', 'Model Trainer', 'Cache Manager', 'Event Logger'];
    const specialistRoles = ['ML Expert', 'Security Analyst', 'Performance Monitor', 'Quality Checker', 'Error Handler'];
    
    for (let i = 0; i < 15; i++) {
      const agentType = i < 2 ? 'coordinator' : (i < 10 ? 'worker' : 'specialist');
      const baseSize = agentType === 'coordinator' ? 6 : (agentType === 'worker' ? 4 : 5);
      
      // Assign specific role based on type
      let role = '';
      if (agentType === 'coordinator') {
        role = coordinatorRoles[i % coordinatorRoles.length];
      } else if (agentType === 'worker') {
        role = workerRoles[(i - 2) % workerRoles.length];
      } else {
        role = specialistRoles[(i - 10) % specialistRoles.length];
      }
      
      initialAgents.push({
        id: i,
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * (agentType === 'coordinator' ? 0.2 : 0.8),
        vy: (Math.random() - 0.5) * (agentType === 'coordinator' ? 0.2 : 0.8),
        size: baseSize + Math.random() * 2,
        type: agentType,
        connections: [],
        activity: Math.random(),
        state: Math.random() > 0.7 ? 'processing' : 'idle',
        workload: Math.random() * 100,
        lastTaskTime: Date.now() - Math.random() * 5000,
        energy: 50 + Math.random() * 50,
        role: role
      });
    }

    // Create realistic hierarchical connections
    const initialConnections: Connection[] = [];
    const coordinators = initialAgents.filter(a => a.type === 'coordinator');
    const workers = initialAgents.filter(a => a.type === 'worker');
    const specialists = initialAgents.filter(a => a.type === 'specialist');
    
    // Coordinators connect to multiple workers
    coordinators.forEach(coordinator => {
      workers.forEach(worker => {
        if (Math.random() > 0.3) {
          initialConnections.push({
            from: coordinator,
            to: worker,
            strength: 0.8 + Math.random() * 0.2,
            pulse: Math.random() * Math.PI * 2
          });
        }
      });
    });
    
    // Workers occasionally connect to specialists
    workers.forEach(worker => {
      specialists.forEach(specialist => {
        if (Math.random() > 0.7) {
          initialConnections.push({
            from: worker,
            to: specialist,
            strength: 0.5 + Math.random() * 0.3,
            pulse: Math.random() * Math.PI * 2
          });
        }
      });
    });
    
    // Some peer-to-peer worker connections
    for (let i = 0; i < workers.length; i++) {
      for (let j = i + 1; j < workers.length; j++) {
        if (Math.random() > 0.8) {
          initialConnections.push({
            from: workers[i],
            to: workers[j],
            strength: 0.3 + Math.random() * 0.4,
            pulse: Math.random() * Math.PI * 2
          });
        }
      }
    }

    setAgents(initialAgents);
    setConnections(initialConnections);

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      // Update and draw connections
      initialConnections.forEach(connection => {
        connection.pulse += 0.05;
        const opacity = (Math.sin(connection.pulse) + 1) * 0.3 + 0.1;
        
        ctx.beginPath();
        ctx.moveTo(connection.from.x, connection.from.y);
        ctx.lineTo(connection.to.x, connection.to.y);
        ctx.strokeStyle = `hsla(210, 100%, 60%, ${opacity * connection.strength})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw pulse along connection
        const progress = (Math.sin(connection.pulse * 2) + 1) * 0.5;
        const pulseX = connection.from.x + (connection.to.x - connection.from.x) * progress;
        const pulseY = connection.from.y + (connection.to.y - connection.from.y) * progress;
        
        ctx.beginPath();
        ctx.arc(pulseX, pulseY, 2, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(210, 100%, 80%, ${opacity})`;
        ctx.fill();
      });

      // Update and draw agents with realistic behaviors
      initialAgents.forEach(agent => {
        const currentTime = Date.now();
        
        // Realistic AI agent state transitions
        if (currentTime - agent.lastTaskTime > 2000) {
          const stateChange = Math.random();
          if (stateChange > 0.95) {
            agent.state = ['idle', 'processing', 'communicating', 'learning'][Math.floor(Math.random() * 4)] as any;
            agent.lastTaskTime = currentTime;
          }
        }
        
        // Update workload and energy realistically
        if (agent.state === 'processing') {
          agent.workload = Math.min(100, agent.workload + 2);
          agent.energy = Math.max(10, agent.energy - 0.5);
        } else if (agent.state === 'idle') {
          agent.workload = Math.max(0, agent.workload - 1);
          agent.energy = Math.min(100, agent.energy + 0.3);
        }
        
        // Movement based on agent type and state
        const speedMultiplier = agent.state === 'processing' ? 0.3 : 
                               agent.state === 'communicating' ? 1.5 : 1.0;
        
        if (agent.type === 'coordinator') {
          // Coordinators move slowly and tend toward center
          const centerX = canvas.offsetWidth / 2;
          const centerY = canvas.offsetHeight / 2;
          agent.vx += (centerX - agent.x) * 0.0001;
          agent.vy += (centerY - agent.y) * 0.0001;
        } else if (agent.type === 'worker') {
          // Workers have more dynamic movement patterns
          agent.vx += (Math.random() - 0.5) * 0.01;
          agent.vy += (Math.random() - 0.5) * 0.01;
        }
        
        // Apply movement with realistic constraints
        agent.x += agent.vx * speedMultiplier;
        agent.y += agent.vy * speedMultiplier;

        // Realistic boundary behavior
        const margin = agent.size * 2;
        if (agent.x <= margin || agent.x >= canvas.offsetWidth - margin) {
          agent.vx *= -0.8; // Some energy loss on bounce
        }
        if (agent.y <= margin || agent.y >= canvas.offsetHeight - margin) {
          agent.vy *= -0.8;
        }

        // Keep in bounds with margin
        agent.x = Math.max(margin, Math.min(canvas.offsetWidth - margin, agent.x));
        agent.y = Math.max(margin, Math.min(canvas.offsetHeight - margin, agent.y));

        // Update activity based on state
        const activitySpeed = agent.state === 'processing' ? 0.05 : 
                             agent.state === 'communicating' ? 0.08 : 0.02;
        agent.activity += activitySpeed;

        // State-based visual effects
        const stateIntensity = {
          idle: 0.4,
          processing: 0.9,
          communicating: 1.0,
          learning: 0.7
        }[agent.state];
        
        const activityPulse = (Math.sin(agent.activity) + 1) * 0.3 * stateIntensity + 0.4;
        
        // Realistic colors based on agent type and state
        const colors = {
          coordinator: `hsla(210, 100%, ${agent.state === 'processing' ? '80' : '60'}%, ${activityPulse})`,
          worker: `hsla(${agent.state === 'communicating' ? '120' : '160'}, 100%, 50%, ${activityPulse})`,
          specialist: `hsla(280, 100%, ${agent.state === 'learning' ? '80' : '70'}%, ${activityPulse})`
        };

        // Draw outer glow
        ctx.beginPath();
        ctx.arc(agent.x, agent.y, agent.size * 2, 0, Math.PI * 2);
        const gradient = ctx.createRadialGradient(
          agent.x, agent.y, 0,
          agent.x, agent.y, agent.size * 2
        );
        gradient.addColorStop(0, colors[agent.type]);
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw agent core
        ctx.beginPath();
        ctx.arc(agent.x, agent.y, agent.size, 0, Math.PI * 2);
        ctx.fillStyle = colors[agent.type];
        ctx.fill();

        // Draw inner highlight
        ctx.beginPath();
        ctx.arc(agent.x - agent.size * 0.3, agent.y - agent.size * 0.3, agent.size * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(0, 0%, 100%, ${activityPulse * 0.8})`;
        ctx.fill();

        // Draw role tag
        const tagPadding = 8;
        const tagHeight = 20;
        const fontSize = 10;
        
        ctx.font = `${fontSize}px Inter, system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        const textMetrics = ctx.measureText(agent.role);
        const tagWidth = textMetrics.width + tagPadding * 2;
        
        // Position tag above the agent
        const tagX = agent.x;
        const tagY = agent.y - agent.size * 2.5 - tagHeight / 2;
        
        // Draw tag background with rounded corners
        ctx.fillStyle = `hsla(0, 0%, 0%, 0.8)`;
        ctx.beginPath();
        ctx.roundRect(tagX - tagWidth / 2, tagY - tagHeight / 2, tagWidth, tagHeight, 4);
        ctx.fill();
        
        // Draw tag border with agent color
        ctx.strokeStyle = colors[agent.type];
        ctx.lineWidth = 1;
        ctx.stroke();
        
        // Draw role text
        ctx.fillStyle = `hsla(0, 0%, 100%, 0.9)`;
        ctx.fillText(agent.role, tagX, tagY);
        
        // Draw connection line from tag to agent
        ctx.beginPath();
        ctx.moveTo(tagX, tagY + tagHeight / 2);
        ctx.lineTo(agent.x, agent.y - agent.size * 1.5);
        ctx.strokeStyle = `hsla(0, 0%, 100%, 0.3)`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-background/50 to-primary/10 rounded-lg">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ background: 'transparent' }}
      />
      
      {/* Overlay content */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 backdrop-blur-sm rounded-full border border-primary/30 mb-4">
            <Brain className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            AI Agent Network
          </h3>
          <p className="text-sm text-muted-foreground">
            Real-time autonomous collaboration
          </p>
        </div>
      </div>

      {/* Network stats overlay */}
      <div className="absolute top-4 right-4 bg-card/80 backdrop-blur-sm rounded-lg border border-primary/20 p-3">
        <div className="flex items-center space-x-2 text-xs">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span className="text-muted-foreground">
            {agents.length} Active Agents
          </span>
        </div>
        <div className="flex items-center space-x-2 text-xs mt-1">
          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
          <span className="text-muted-foreground">
            {connections.length} Connections
          </span>
        </div>
      </div>
    </div>
  );
};

export default AIAgentNetwork;
